// Utilidades puras de disponibilidad semanal.
//
// Feature: gestion-jugadores-disponibilidad-categorias
//
// Las horas se manejan internamente como minutos desde medianoche (0-1439),
// consistente con el modelo de datos del backend (tblDisponibilidadSemanal).
// La UI trabaja con cadenas "HH:mm"; estas funciones convierten entre ambos
// formatos y validan rango y solapamiento. Se mantienen puras (sin I/O ni
// dependencias de Vue) para poder cubrirlas con unit/property tests.

export interface Franja {
  /** Identificador de la franja (0 al crear). */
  idDisponibilidad?: number;
  /** Día de la semana 1-7 (Lunes..Domingo). */
  idDia: number;
  /** Hora de inicio en minutos desde medianoche (0-1439). */
  horaDesde: number;
  /** Hora de fin en minutos desde medianoche (0-1439). */
  horaHasta: number;
}

/**
 * Convierte una cadena "HH:mm" a minutos desde medianoche.
 * Devuelve NaN si el formato es inválido o está fuera de rango.
 */
export function hhmmAMinutos(hhmm: string): number {
  if (typeof hhmm !== "string") {
    return NaN;
  }
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) {
    return NaN;
  }
  const horas = Number(match[1]);
  const minutos = Number(match[2]);
  if (horas < 0 || horas > 23 || minutos < 0 || minutos > 59) {
    return NaN;
  }
  return horas * 60 + minutos;
}

/**
 * Convierte minutos desde medianoche (0-1439) a una cadena "HH:mm".
 * Devuelve cadena vacía si el valor está fuera de rango o no es entero.
 */
export function minutosAHhmm(min: number): string {
  if (
    typeof min !== "number" ||
    !Number.isInteger(min) ||
    min < 0 ||
    min > 1439
  ) {
    return "";
  }
  const horas = Math.floor(min / 60);
  const minutos = min % 60;
  const dosDigitos = (n: number) => (n < 10 ? "0" + n : String(n));
  return `${dosDigitos(horas)}:${dosDigitos(minutos)}`;
}

/**
 * Un rango es válido si y solo si desde < hasta (ambos en minutos).
 * Validates: Requirements 3.4
 */
export function esRangoValido(desde: number, hasta: number): boolean {
  if (
    typeof desde !== "number" ||
    typeof hasta !== "number" ||
    Number.isNaN(desde) ||
    Number.isNaN(hasta)
  ) {
    return false;
  }
  return desde < hasta;
}

/**
 * Indica si dos intervalos [aDesde, aHasta) y [bDesde, bHasta) se solapan.
 * Dos franjas se solapan cuando aDesde < bHasta && bDesde < aHasta.
 */
export function seSolapan(
  aDesde: number,
  aHasta: number,
  bDesde: number,
  bHasta: number,
): boolean {
  return aDesde < bHasta && bDesde < aHasta;
}

/**
 * Indica si una franja nueva se solapa con alguna de las existentes del
 * mismo día. La comparación es por rango horario; el llamador debe pasar
 * únicamente las franjas del mismo idDia. Se ignora la franja que comparte
 * idDisponibilidad (caso edición).
 * Validates: Requirements 3.5
 */
export function haySolapamiento(
  franjaNueva: Franja,
  franjasExistentes: Franja[],
): boolean {
  if (!Array.isArray(franjasExistentes)) {
    return false;
  }
  return franjasExistentes.some((f) => {
    if (f.idDia !== franjaNueva.idDia) {
      return false;
    }
    if (
      franjaNueva.idDisponibilidad != null &&
      f.idDisponibilidad != null &&
      f.idDisponibilidad === franjaNueva.idDisponibilidad
    ) {
      return false;
    }
    return seSolapan(
      franjaNueva.horaDesde,
      franjaNueva.horaHasta,
      f.horaDesde,
      f.horaHasta,
    );
  });
}
